import type { Firestore } from 'firebase-admin/firestore';
import type { RedisClient } from '../../core/redis/client';
import type { BingoCard } from '@my-roadtrip/types';
import { generateBingoChallenges } from './lib/challenge-generator';
import { safeJsonParse } from '../../utils/sanitize.js';
import { createLogger } from '../../common/utils/logger';

const logger = createLogger('BingoService');

export class BingoService {
  private readonly firestore: Firestore;
  private readonly redis: RedisClient;
  private readonly bingoCollection = 'bingo_cards';

  constructor(firestore: Firestore, redis: RedisClient) {
    this.firestore = firestore;
    this.redis = redis;
  }

  /**
   * Creates a new Bingo Card for a given plan.
   * @param planId - The ID of the plan.
   * @param userId - The ID of the user creating the card.
   * @returns The newly created BingoCard.
   */
  async createBingoCard(planId: string, userId: string): Promise<BingoCard> {
    const existingCard = await this.getBingoCardByPlanId(planId);
    if (existingCard) {
      // Potentially check for ownership or other logic here
      return existingCard;
    }

    const challenges = generateBingoChallenges();

    const newBingoCard: Omit<BingoCard, 'id'> = {
      planId,
      challenges,
      createdAt: new Date(),
      createdBy: userId,
    };

    const docRef = await this.firestore.collection(this.bingoCollection).add(newBingoCard);

    const createdCard: BingoCard = {
      id: docRef.id,
      ...newBingoCard,
    };

    // Cache the new card
    await this.redis.set(`bingo-card:${planId}`, JSON.stringify(createdCard), { ex: 3600 }); // Cache for 1 hour

    return createdCard;
  }

  /**
   * Retrieves a Bingo Card by its associated plan ID.
   * @param planId - The ID of the plan.
   * @returns The BingoCard, or null if not found.
   */
  async getBingoCardByPlanId(planId: string): Promise<BingoCard | null> {
    // Check cache first
    const cachedCard = await this.redis.get(`bingo-card:${planId}`);
    if (cachedCard) {
      const parsed = safeJsonParse<BingoCard>(cachedCard);
      if (parsed) {
        return parsed;
      }
    }

    const snapshot = await this.firestore
      .collection(this.bingoCollection)
      .where('planId', '==', planId)
      .limit(1)
      .get();

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const card = { id: doc.id, ...doc.data() } as BingoCard;

    // Cache the result
    await this.redis.set(`bingo-card:${planId}`, JSON.stringify(card), { ex: 3600 });

    return card;
  }

  /**
   * Marks a specific bingo challenge as complete.
   * @param challengeId - The ID of the challenge to complete.
   * @param userId - The ID of the user completing the challenge.
   * @returns The updated BingoCard.
   */
  async completeChallenge(challengeId: string, userId: string): Promise<BingoCard | null> {
    // This is a simplified implementation. A real implementation would need to:
    // 1. Find which BingoCard the challenge belongs to.
    // 2. Update the specific challenge in the array.
    // 3. Award points to the user.

    // For now, we'll assume we can find the card and update it.
    // This logic would need to be more robust.
    
    // As this is a more complex operation (querying sub-arrays), a more detailed implementation
    // is required. For this step, we'll focus on the service structure.
    logger.info('User completed bingo challenge', { userId, challengeId });
    
    // Placeholder: In a real scenario, you would fetch the card, update the challenge,
    // save it, and return the updated card.
    return null;
  }
}
